import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import API from '../stores/API'
import { Sample, SampleLinkage, Template } from '../stores/Sample/index.d'

// Query Keys for samples
export const sampleQueryKeys = {
  all: ['samples'] as const,
  lists: () => [...sampleQueryKeys.all, 'list'] as const,
  list: (filters: Object) => [...sampleQueryKeys.lists(), filters] as const,
  latest: (filters: Object) => [...sampleQueryKeys.all, 'latest', filters] as const,
  detail: (id: number) => [...sampleQueryKeys.all, 'detail', id] as const,
  history: (id: number, showAll: boolean) => [...sampleQueryKeys.all, 'history', id, showAll] as const,
  linkages: ['samples', 'linkages'] as const,
  chartSamples: (filters: Object) => [...sampleQueryKeys.all, 'chart', filters] as const,
  chartTemplates: ['samples', 'chart', 'templates'] as const,
  chartTemplate: (id: number) => [...sampleQueryKeys.all, 'chart', 'template', id] as const
}

// API functions that mirror SampleStore methods
const getSamples = async (filters: Object): Promise<Sample[]> => {
  const response = await API.get('/samples', { params: filters })
  return response.data
}

const getLatestSamples = async (filters: Object): Promise<Sample[]> => {
  const response = await API.get('/samples/latest', { params: filters })
  return response.data
}

const getSample = async (id: number): Promise<Sample> => {
  const response = await API.get(`/samples/${id}`)
  return response.data
}

const getSampleHistory = async (id: number, showAll: boolean = false): Promise<any> => {
  const response = await API.get(`/samples/${id}/history`, { params: { showAll } })
  return response.data
}

const getLinkages = async (): Promise<SampleLinkage[]> => {
  const response = await API.get('/samples/latest/linkages')
  return response.data
}

const createLinkage = async (customerIds: number[]): Promise<SampleLinkage[]> => {
  const response = await API.post('/samples/latest/linkages', { customerInclusions: customerIds })
  return response.data
}

const removeLinkage = async (customerId: number): Promise<SampleLinkage[]> => {
  const response = await API.delete('/samples/latest/linkages', {
    params: { customerInclusions: customerId }
  })
  return response.data
}

const getChartSamples = async (filters: Object): Promise<any> => {
  const response = await API.get('/samples/advanced', { params: filters })
  return response.data
}

const getChartTemplates = async (): Promise<Template[]> => {
  const response = await API.get('/samples/graph/templates')
  return response.data
}

const createChartTemplate = async (name: string, ids: number[]): Promise<Template> => {
  const template = {
    testItemIdList: ids,
    name,
    added: new Date(),
    addedBy: 'anonym'
  }
  const response = await API.post('/samples/graph/templates', template)
  return response.data
}

const updateChartTemplate = async (id: number, name: string): Promise<void> => {
  await API.put(`/samples/graph/templates/${id}`, { name })
}

const removeChartTemplate = async (id: number): Promise<void> => {
  await API.delete(`/samples/graph/templates/${id}`)
}

const sendMessage = async (
  message: string,
  customer: string,
  unit: string,
  component: string,
  sampleNumber: number
): Promise<any> => {
  const response = await API.post('/samples/message', {
    message,
    customer,
    unit,
    component,
    sampleNumber
  })
  return response.data
}

const actionSample = async (id: number, processed: boolean): Promise<void> => {
  await API.put(`/samples/${id}/processed`, { processed })
}

const actionSamples = async (sampleNumbers: number[], processed: boolean = true): Promise<void> => {
  await API.put('/samples/processed', { sampleNumbers, processed })
}

// React Query hooks
export const useSamples = (filters: Object = {}) => {
  return useQuery({
    queryKey: sampleQueryKeys.list(filters),
    queryFn: () => getSamples(filters),
    staleTime: 2 * 60 * 1000 // 2 minutes - samples can change frequently
  })
}

export const useLatestSamples = (filters: Object = {}) => {
  return useQuery({
    queryKey: sampleQueryKeys.latest(filters),
    queryFn: () => getLatestSamples(filters),
    staleTime: 1 * 60 * 1000 // 1 minute - latest samples change very frequently
  })
}

export const useSample = (id: number) => {
  return useQuery({
    queryKey: sampleQueryKeys.detail(id),
    queryFn: () => getSample(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000 // 5 minutes - individual samples don't change often
  })
}

export const useSampleHistory = (id: number, showAll: boolean = false) => {
  return useQuery({
    queryKey: sampleQueryKeys.history(id, showAll),
    queryFn: () => getSampleHistory(id, showAll),
    enabled: !!id,
    staleTime: 10 * 60 * 1000 // 10 minutes - history doesn't change
  })
}

export const useLinkages = () => {
  return useQuery({
    queryKey: sampleQueryKeys.linkages,
    queryFn: getLinkages,
    staleTime: 5 * 60 * 1000 // 5 minutes - linkages can change
  })
}

export const useChartSamples = (filters: Object = {}) => {
  return useQuery({
    queryKey: sampleQueryKeys.chartSamples(filters),
    queryFn: () => getChartSamples(filters),
    staleTime: 5 * 60 * 1000 // 5 minutes - chart samples can change
  })
}

export const useChartTemplates = () => {
  return useQuery({
    queryKey: sampleQueryKeys.chartTemplates,
    queryFn: getChartTemplates,
    staleTime: 30 * 60 * 1000 // 30 minutes - templates rarely change
  })
}

// Mutations
export const useCreateLinkage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createLinkage,
    onSuccess: () => {
      // Invalidate and refetch linkages after successful creation
      queryClient.invalidateQueries({ queryKey: sampleQueryKeys.linkages })
    }
  })
}

export const useRemoveLinkage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: removeLinkage,
    onSuccess: () => {
      // Invalidate and refetch linkages after successful removal
      queryClient.invalidateQueries({ queryKey: sampleQueryKeys.linkages })
    }
  })
}

export const useCreateChartTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ name, ids }: { name: string; ids: number[] }) => createChartTemplate(name, ids),
    onSuccess: () => {
      // Invalidate and refetch chart templates after successful creation
      queryClient.invalidateQueries({ queryKey: sampleQueryKeys.chartTemplates })
    }
  })
}

export const useUpdateChartTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => updateChartTemplate(id, name),
    onSuccess: () => {
      // Invalidate and refetch chart templates after successful update
      queryClient.invalidateQueries({ queryKey: sampleQueryKeys.chartTemplates })
    }
  })
}

export const useRemoveChartTemplate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: removeChartTemplate,
    onSuccess: () => {
      // Invalidate and refetch chart templates after successful removal
      queryClient.invalidateQueries({ queryKey: sampleQueryKeys.chartTemplates })
    }
  })
}

export const useSendMessage = () => {
  return useMutation({
    mutationFn: ({
      message,
      customer,
      unit,
      component,
      sampleNumber
    }: {
      message: string
      customer: string
      unit: string
      component: string
      sampleNumber: number
    }) => sendMessage(message, customer, unit, component, sampleNumber)
  })
}

export const useActionSample = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, processed }: { id: number; processed: boolean }) => actionSample(id, processed),
    onSuccess: () => {
      // Invalidate and refetch samples after successful action
      queryClient.invalidateQueries({ queryKey: sampleQueryKeys.all })
    }
  })
}

export const useActionSamples = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      sampleNumbers,
      processed = true
    }: {
      sampleNumbers: number[]
      processed?: boolean
    }) => actionSamples(sampleNumbers, processed),
    onSuccess: () => {
      // Invalidate and refetch samples after successful action
      queryClient.invalidateQueries({ queryKey: sampleQueryKeys.all })
    }
  })
} 